/* eslint-disable @typescript-eslint/no-explicit-any */

import React, { useState, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, InputTextField } from '@/components';
import { Depo20FormData } from '@/types/depo20';
import { useToastNotification } from '@/hooks';

import { faSave, faStar } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ModalDetailSelectFavClient from './components/ModalDetailSelectFavClient';

import styles from './Depo20.module.scss';
import { comprobarDocumento } from './services/comprobarDocumento';

export interface Depo20FormState {
  rut?: string | null;
  nombre: string | null;
  referencia?: string;
}

interface Depo20FormErrors {
  rut: string;
  nombre: string;
  referencia: string;
}

interface Depo20ClientFormProps {
  formClientData: Depo20FormData | undefined;
  formDataLoading: boolean;
  fetchAddLiquidacionLoading: boolean;
  saveLiquidacion: () => void;
  setFormDataValues: (val: Depo20FormState) => void;
  formDataValue: Depo20FormState;
}

const Depo20ClientForm: React.FC<Depo20ClientFormProps> = ({
  formClientData,
  formDataLoading,
  fetchAddLiquidacionLoading,
  saveLiquidacion,
  formDataValue,
  setFormDataValues,
}) => {
  const { t } = useTranslation('depo20');
  const { showToast } = useToastNotification();

  const [formErrors, setFormErrors] = useState<Depo20FormErrors>({
    rut: '',
    nombre: '',
    referencia: '',
  });

  const [isDialogShown, setIsDialogShown] = useState(false);

  const toggleDialog = () => {
    setIsDialogShown(!isDialogShown);
  };

  const handleInputChange = (name: string, value: string) => {
    if (name === 'rut') {
      setFormDataValues({ ...formDataValue, rut: value });
    } else if (name === 'nombre') {
      setFormDataValues({ ...formDataValue, nombre: value });
    } else if (name === 'referencia') {
      setFormDataValues({ ...formDataValue, referencia: value });
    }
  };

  const validateForm = async () => {
    const errors: Depo20FormErrors = { rut: '', nombre: '', referencia: '' };
    let formIsValid = true;

    if (!formDataValue?.rut?.match(/^\d{12}$/)) {
      errors.rut = t('documentValidation');
      formIsValid = false;
    }
    const response = await comprobarDocumento({
      documento: formDataValue?.rut ? formDataValue.rut : '',
      tipo: 2,
    });
    if (response === 1) {
      errors.rut =
        'El RUT ingresado pertenece a un titular sin dirección. En el proceso de factura electrónica es necesario que el titular contenga el dato mencionado. Comuníquese con atención al cliente para regularizar la información';
      formIsValid = false;
    }

    if (response === 2) {
      errors.rut = 'El RUT ingresado es inválido';
      formIsValid = false;
    }

    setFormErrors(errors);
    return formIsValid;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const validForm = await validateForm();
    if (validForm) {
      saveLiquidacion();
    }
  };

  const addToFavourites = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    validateForm();
    const validForm = await validateForm();
    if (validForm) {
      e.preventDefault();
      window.localStorage.setItem(
        `rutFavoritoLiquidacion ${formClientData?.oid}`,
        formDataValue?.rut ?? ''
      );

      window.localStorage.setItem(
        `nombreFavoritoLiquidacion ${formClientData?.oid}`,
        formDataValue.nombre ?? ''
      );

      showToast({
        severity: 'success',
        detail: t('favClientAdded'),
      });
    }
  };

  return (
    <div className={styles.container}>
      <ModalDetailSelectFavClient
        oid={Number(formDataValue.rut)}
        isDialogShown={isDialogShown}
        toggleDialog={toggleDialog}
      />
      <form onSubmit={handleSubmit} className="p-fluid">
        <div className={styles.clientFormContainer}>
          <InputTextField
            id="rut"
            name="rut"
            value={formDataValue?.rut ?? ''}
            onChange={(e) => handleInputChange('rut', e.target.value)}
            placeholder={t('rutPlaceholder')}
            label={t('rut')}
            error={formErrors.rut}
            disabled={
              formDataLoading ||
              (formClientData &&
                !(
                  formClientData?.consumidorFinal === false &&
                  formClientData?.estado === 'En Edición'
                ))
            }
            longErrorMessage
          >
            <Button
              onClick={(e) => addToFavourites(e)}
              className={`text-blue-800 p-button-outlined align-items-center justify-content-center ml-3 px-3 ${styles.starButton}`}
              severity="danger"
            >
              <FontAwesomeIcon icon={faStar} />
            </Button>
          </InputTextField>

          <InputTextField
            id="nombre"
            name="nombre"
            value={formDataValue.nombre ?? ''}
            onChange={(e) => handleInputChange('nombre', e.target.value)}
            placeholder={t('namePlaceholder')}
            label={t('name')}
            error={formErrors.nombre}
            disabled={formDataLoading}
          />
        </div>

        <InputTextField
          id="referencia"
          name="referencia"
          value={formDataValue.referencia ?? ''}
          onChange={(e) => handleInputChange('referencia', e.target.value)}
          placeholder={t('referencePlaceholder')}
          label={t('reference')}
          error={formErrors.referencia}
          disabled={formDataLoading}
        />

        <Button
          className={`ml-auto ${styles.saveButton}`}
          loading={formDataLoading || fetchAddLiquidacionLoading}
          green
        >
          <FontAwesomeIcon icon={faSave} className="mr-2" />
          {t('saveInformation')}
        </Button>
      </form>
    </div>
  );
};

export default Depo20ClientForm;
